import React from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Operators from './pages/Operators';
import Brands from './pages/Brands';
import BrandNetworks from './pages/BrandNetworks';
import Users from './pages/Users';
import WorldMap from './pages/WorldMap';
import Logs from './pages/Logs';
import UnknownNetworks from './pages/UnknownNetworks';
import Settings from './pages/Settings';
import TestMap from './pages/TestMap';
import LeafletMap from './pages/LeafletMap';
import LeafletMapWithCountries from './pages/LeafletMapWithCountries';
import SimpleColoredMap from './pages/SimpleColoredMap';
import CountryGrid from './pages/CountryGrid';

/** Renders the console only once somebody is signed in.
 *
 * This is a convenience, not the security boundary -- every endpoint the
 * console calls checks the session itself, so a hand-crafted URL gets a 401
 * from the server rather than data. */
const RequireAuth: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }
  if (!user) {
    return <Login />;
  }
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

/** Only an administrator with no brand restriction may manage users. */
const RequireSuperAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// A data router, not <BrowserRouter>. useBlocker -- which is what stops Brand
// Networks from silently discarding unsaved edits when you navigate away --
// only works under one, and throws outright otherwise.
const router = createBrowserRouter([
  {
    element: <RequireAuth />,
    children: [
      { path: '/', element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <Dashboard /> },
      { path: '/operators', element: <Operators /> },
      { path: '/brands', element: <Brands /> },
      // The brand travels in the query string, not the path: the sidebar
      // highlights on an exact pathname match, and a path segment would
      // silently break it.
      { path: '/brand-networks', element: <BrandNetworks /> },
      { path: '/users', element: <RequireSuperAdmin><Users /></RequireSuperAdmin> },
      { path: '/world-map', element: <CountryGrid /> },
      { path: '/world-map-old', element: <WorldMap /> },
      { path: '/test-map', element: <TestMap /> },
      { path: '/logs', element: <Logs /> },
      { path: '/unknown-networks', element: <UnknownNetworks /> },
      { path: '/settings', element: <Settings /> },
    ],
  },
]);

function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}

export default App;
